import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Ticket, 
  Monitor, 
  FileText, 
  Clock,
  CheckCircle2,
  Activity,
  Users,
  HardDrive,
  Wifi,
  Shield,
  Server,
  Key,
  Plus,
  ChevronRight,
  ChevronDown,
  BarChart2,
  Building2,
  Settings,
  X,
  Layout,
  MessageSquare,
  Inbox
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { itSupportData } from './data/itSupportData';
import OverviewTab from './components/overview/OverviewTab';
import TicketsTab from './components/tickets/TicketsTab';
import AssetsTab from './components/assets/AssetsTab';
import DocumentationTab from './components/docs/DocumentationTab';
import CommunicationsTab from './components/communications/CommunicationsTab';
import SystemStatusButton from './components/shared/SystemStatusButton';

// Badge Components
const PriorityBadge = ({ priority }) => {
  const colors = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${colors[priority]}`}>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    open: 'bg-blue-100 text-blue-700',
    'in-progress': 'bg-yellow-100 text-yellow-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-700'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${colors[status]}`}>
      {status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
    </span>
  );
};

const ITSupportDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Layout },
    { id: 'tickets', label: 'Tickets', icon: Ticket },
    { id: 'assets', label: 'Assets', icon: Monitor },
    { id: 'docs', label: 'Documentation', icon: FileText },
    { id: 'communications', label: 'Internal Comms', icon: MessageSquare }
  ];

  return (
    <div className="space-y-6">
      {/* Header with System Status */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">IT Support Center</h1>
            <p className="text-blue-100 mt-1">Manage IT support, assets, and documentation</p>
          </div>
          <Link
            to="/it-support/tickets/new"
            className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
          >
            <Plus className="w-5 h-5" />
            Create Ticket
          </Link>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex space-x-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600'
                    : 'text-white/90 hover:bg-white/10'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'tickets' && <TicketsTab />}
        {activeTab === 'assets' && <AssetsTab />}
        {activeTab === 'docs' && <DocumentationTab />}
        {activeTab === 'communications' && <CommunicationsTab />}
      </div>

      {/* Floating Status Button */}
      <SystemStatusButton />
    </div>
  );
};

export default ITSupportDashboard;
